<template>
    <div class="audioContainer">
        <div class="duration">{{ timePassed != '0.00' ? timePassed : message?.content?.audio?.duration }}</div>
        <div v-show="audioReady" ref="audioContainer"></div>
        <img v-if="!audioReady" :src="loadFile(images.wave)" height="40" class="w-1oo">
        <img v-show="!audioLoading" @click="playPauseAudio" :src="loadFile(images[audioPlaying ? 'pause' : 'play'])"
            height="36" width="36">
        <span v-if="audioLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
    </div>
</template>

<script setup>
import { watch, ref, onMounted } from 'vue'
import WaveSurfer from 'wavesurfer.js'
import lodash from 'lodash'
import { loadFile, fetchAudioMetadata, formatTimeDuration } from '@/components/utils.js'
import { useStore } from 'vuex';

const props = defineProps({
    message: {
        type: Object,
        default: () => ({}),
        required: true,
    },
});

const store = useStore();

const audioSrc = ref('')
const audioContainer = ref(null)
const audioPlaying = ref(false)
const audioLoading = ref(false)
const audioReady = ref(false)
const timePassed = ref('0.00')
let wavesurfer = null

const images = {
    play: 'play.svg',
    pause: 'pause.svg',
    wave: 'wave.svg'
}

const playPauseAudio = () => {
    if (!audioReady.value) {
        audioSrc.value = props.message.content.audio.url
        wavesurfer.load(audioSrc.value);
        audioLoading.value = true
        return
    }
    wavesurfer.playPause()
}

const throttledLeftTime = lodash.throttle(() => {
    timePassed.value = formatTimeDuration(wavesurfer.getCurrentTime());
}, 1001);

const initWaveSurfer = () => {
    if (wavesurfer) {
        wavesurfer.destroy();
    }
    wavesurfer = WaveSurfer.create({
        container: audioContainer.value,
        waveColor: '#6d6d6d',
        progressColor: '#ec552f',
        height: 40,
        cursorColor: 'transparent',
        cursorWidth: 0,
        barWidth: 3,
        barGap: 3,
        barRadius: 3,
    })

    wavesurfer.on('play', () => {
        audioPlaying.value = true
    })

    wavesurfer.on('finish', () => {
        audioPlaying.value = false
    })

    wavesurfer.on('pause', () => {
        audioPlaying.value = false
    })

    wavesurfer.on('ready', () => {
        audioLoading.value = false
        audioReady.value = true
        playPauseAudio()
    });
    wavesurfer.on('audioprocess', throttledLeftTime);
}

watch(() => props.message, (newVal, oldVal) => {
    if (newVal.content.audio.url !== oldVal.content.audio.url) {
        initWaveSurfer();
    }
}, { deep: true });

onMounted(async () => {
    initWaveSurfer()
    if (props?.message?.content?.audio?.duration == undefined) {
        const audioUrl = props?.message?.content?.audio?.url
        const duration = await fetchAudioMetadata(audioUrl)
        const updatedMessage = { ...props.message }
        updatedMessage.content.audio.duration = formatTimeDuration(duration ?? 0)
        store.commit('Chat/updateMessage', updatedMessage);
    }
})

</script>

<style scoped lang="scss">
@import './styles/audio.scss';
</style>
